import { Box, Divider, Sheet, Typography, useColorScheme } from "@mui/joy";
import React, { Fragment, useEffect, useRef, useState } from "react";
import {
  hatchlingCharacters,
  hatchlingLocations,
  hatchlingPoems,
} from "../../data/hatchlingData";
import { Chapter } from "../../types/novellaTypes";
import {
  getReadPoems,
  hatchlingChapterToPoemMapping,
  isChapterCompleted,
  markChapterAsCompleted,
  markPoemAsRead,
} from "../../utils/cookieUtils";
import HatchlingCharacterName from "./HatchlingCharacterName";
import HatchlingLocationName from "./HatchlingLocationName";
import HatchlingPoemDisplay from "./HatchlingPoemDisplay";
import Toast from "../NovellaReader/Toast";

interface HatchlingReaderContentProps {
  chapter: Chapter;
  onComplete?: () => void;
}

const HatchlingReaderContent = ({
  chapter,
  onComplete,
}: HatchlingReaderContentProps) => {
  const { mode } = useColorScheme();
  const isDark = mode === "dark";
  const contentRef = useRef<HTMLDivElement>(null);
  const [processedContent, setProcessedContent] =
    useState<React.ReactNode>(null);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [isChapterComplete, setIsChapterComplete] = useState<boolean>(false);
  const [chapterEnded, setChapterEnded] = useState<boolean>(false);

  // Check if chapter is already completed
  useEffect(() => {
    const completed = isChapterCompleted(chapter.id);
    setIsChapterComplete(completed);

    // Reset state when chapter changes
    setChapterEnded(false);
    setShowToast(false);
  }, [chapter.id]);

  // When the user scrolls to 85% of the page, consider the chapter completed
  useEffect(() => {
    const handleScroll = () => {
      if (chapterEnded || isChapterComplete) return;

      const { scrollTop, scrollHeight, clientHeight } =
        document.documentElement;
      const scrollPosition = scrollTop + clientHeight;
      const scrollThreshold = scrollHeight * 0.85;

      if (scrollPosition >= scrollThreshold) {
        markChapterAsCompleted(chapter.id);
        if (onComplete) onComplete();

        setChapterEnded(true);
        setIsChapterComplete(true);

        // Check if completing this chapter unlocks a poem
        const correspondingPoemId = hatchlingChapterToPoemMapping[chapter.id];
        if (correspondingPoemId) {
          markPoemAsRead(correspondingPoemId);
          setTimeout(() => {
            setShowToast(true);
          }, 300);
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [chapter.id, chapterEnded, isChapterComplete, onComplete]);

  // Process the content to add interactive elements
  useEffect(() => {
    const processText = (text: string) => {
      if (!text) return null;

      const processedText = text;

      // Create maps of character and location names for quick lookup
      const characterNameMap: Record<string, string> = {};
      const locationMap: Record<string, string> = {};

      Object.entries(hatchlingCharacters).forEach(([id, character]) => {
        characterNameMap[character.name.toLowerCase()] = id;
        character.alternateNames.forEach((altName) => {
          if (altName) {
            characterNameMap[altName.toLowerCase()] = id;
          }
        });
      });

      Object.entries(hatchlingLocations).forEach(([id, location]) => {
        locationMap[location.name.toLowerCase()] = id;
      });

      const paragraphs = processedText.split("\n\n");
      return paragraphs
        .map((paragraph, paragraphIndex) => {
          if (paragraph.trim() === "") return null;

          // Handle dividers
          if (paragraph.startsWith("***")) {
            return (
              <Box
                key={`divider-${paragraphIndex}`}
                sx={{
                  textAlign: "center",
                  my: 4,
                  color: isDark ? "neutral.400" : "neutral.600",
                }}
              >
                <Typography level="body-md">***</Typography>
              </Box>
            );
          }

          // Handle uppercase headers
          if (paragraph.match(/^[A-Z\s]+$/)) {
            return (
              <Typography
                key={`header-${paragraphIndex}`}
                level="title-md"
                sx={{
                  fontFamily: "'Cinzel', serif",
                  textAlign: "center",
                  mt: 4,
                  mb: 2,
                }}
              >
                {paragraph}
              </Typography>
            );
          }

          // Handle poems/verses - only multi-line quoted text with verse structure
          // Must have at least 3 newlines to be considered a poem (multiple verses)
          const newlineCount = (paragraph.match(/\n/g) || []).length;
          const isPoem =
            paragraph.startsWith('"') &&
            newlineCount >= 3 &&
            paragraph.endsWith('"');

          if (isPoem) {
            return (
              <Box
                key={`poem-${paragraphIndex}`}
                sx={{ my: 3, mx: { xs: 2, sm: 6 } }}
              >
                <Typography
                  level="body-md"
                  sx={{
                    fontFamily: "'EB Garamond', serif",
                    fontStyle: "italic",
                    whiteSpace: "pre-line",
                    textAlign: "center",
                    lineHeight: 1.8,
                  }}
                >
                  {paragraph}
                </Typography>
              </Box>
            );
          }

          // Helper function to process a text segment for character/location names
          const processSegment = (
            text: string,
            segmentIndex: number,
            isItalic: boolean
          ): React.ReactNode[] => {
            const words = text.split(/(\s+)/);
            return words.map((word, wordIndex) => {
              if (word.trim() === "") {
                return (
                  <Fragment
                    key={`space-${paragraphIndex}-${segmentIndex}-${wordIndex}`}
                  >
                    {isItalic ? <em>{word}</em> : word}
                  </Fragment>
                );
              }

              const lowerWord = word.toLowerCase();

              // Search for character names
              for (const [characterNameLower, characterId] of Object.entries(
                characterNameMap
              )) {
                if (characterNameLower.length < 3) continue;

                const index = lowerWord.indexOf(characterNameLower);
                if (index !== -1) {
                  const nameLength = characterNameLower.length;

                  if (
                    index === 0 ||
                    index + nameLength === word.length ||
                    (index > 0 && !/[a-zA-Z]/.test(word[index - 1])) ||
                    (index + nameLength < word.length &&
                      !/[a-zA-Z]/.test(word[index + nameLength]))
                  ) {
                    const prefix = word.substring(0, index);
                    const match = word.substring(index, index + nameLength);
                    const suffix = word.substring(index + nameLength);

                    return (
                      <Fragment
                        key={`char-part-${paragraphIndex}-${segmentIndex}-${wordIndex}`}
                      >
                        {isItalic ? <em>{prefix}</em> : prefix}
                        <HatchlingCharacterName id={characterId}>
                          {isItalic ? <em>{match}</em> : match}
                        </HatchlingCharacterName>
                        {isItalic ? <em>{suffix}</em> : suffix}
                      </Fragment>
                    );
                  }
                }
              }

              // Search for location names
              for (const [locationNameLower, locationId] of Object.entries(
                locationMap
              )) {
                if (locationNameLower.length < 3) continue;

                const index = lowerWord.indexOf(locationNameLower);
                if (index !== -1) {
                  const nameLength = locationNameLower.length;

                  if (
                    index === 0 ||
                    index + nameLength === word.length ||
                    (index > 0 && !/[a-zA-Z]/.test(word[index - 1])) ||
                    (index + nameLength < word.length &&
                      !/[a-zA-Z]/.test(word[index + nameLength]))
                  ) {
                    const prefix = word.substring(0, index);
                    const match = word.substring(index, index + nameLength);
                    const suffix = word.substring(index + nameLength);

                    return (
                      <Fragment
                        key={`loc-part-${paragraphIndex}-${segmentIndex}-${wordIndex}`}
                      >
                        {isItalic ? <em>{prefix}</em> : prefix}
                        <HatchlingLocationName id={locationId}>
                          {isItalic ? <em>{match}</em> : match}
                        </HatchlingLocationName>
                        {isItalic ? <em>{suffix}</em> : suffix}
                      </Fragment>
                    );
                  }
                }
              }

              return (
                <Fragment
                  key={`word-${paragraphIndex}-${segmentIndex}-${wordIndex}`}
                >
                  {isItalic ? <em>{word}</em> : word}
                </Fragment>
              );
            });
          };

          // Process thoughts (text between single quotes) as italic
          // Match thoughts that start with ' followed by uppercase and end with punctuation + '
          const thoughtPattern = /'[A-Z][^']*[!?.,…']'/g;
          const parts: React.ReactNode[] = [];
          let lastIndex = 0;
          let match;
          let segmentIndex = 0;

          while ((match = thoughtPattern.exec(paragraph)) !== null) {
            // Add text before the thought
            if (match.index > lastIndex) {
              parts.push(
                ...processSegment(
                  paragraph.slice(lastIndex, match.index),
                  segmentIndex++,
                  false
                )
              );
            }
            // Add the thought in italic
            parts.push(
              ...processSegment(match[0], segmentIndex++, true)
            );
            lastIndex = match.index + match[0].length;
          }

          // Add remaining text after last thought
          if (lastIndex < paragraph.length) {
            parts.push(
              ...processSegment(paragraph.slice(lastIndex), segmentIndex++, false)
            );
          }

          const content = parts.length > 0 ? parts : processSegment(paragraph, 0, false);

          return (
            <Typography
              key={`paragraph-${paragraphIndex}`}
              level="body-md"
              sx={{
                fontFamily: "'EB Garamond', serif",
                mb: 2,
                textIndent: "2em",
                lineHeight: "1.7",
              }}
            >
              {content}
            </Typography>
          );
        })
        .filter(Boolean);
    };

    setProcessedContent(processText(chapter.content));
  }, [chapter, isDark]);

  return (
    <Sheet
      variant="plain"
      sx={{
        p: { xs: 3, md: 5 },
        borderRadius: "lg",
        bgcolor: isDark ? "neutral.900" : "background.surface",
        maxWidth: "900px",
        mx: "auto",
        boxShadow: "md",
        border: "1px solid",
        borderColor: isDark ? "neutral.800" : "neutral.100",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Typography
        level="h2"
        component="h1"
        sx={{
          fontFamily: "'Cinzel', serif",
          textAlign: "center",
          mb: 5,
          borderBottom: "1px solid",
          borderColor: "divider",
          pb: 2,
          fontSize: { xs: "1.8rem", md: "2.2rem" },
          letterSpacing: "0.02em",
          fontWeight: "bold",
        }}
      >
        {chapter.title}
      </Typography>

      {/* Display all unlocked poems in Poems section */}
      {chapter.containsAllPoems && (
        <div id="poems-section">
          <Typography
            level="body-lg"
            sx={{
              textAlign: "center",
              mb: 4,
              fontFamily: "'EB Garamond', serif",
              color: isDark ? "neutral.300" : "neutral.700",
              fontStyle: "italic",
            }}
          >
            These are the poems you've unlocked throughout your journey.
          </Typography>

          {getReadPoems().length === 0 ? (
            <Box sx={{ textAlign: "center", my: 8 }}>
              <Typography
                level="body-lg"
                sx={{
                  fontStyle: "italic",
                  color: isDark ? "neutral.400" : "neutral.600",
                }}
              >
                No poems discovered yet. Continue reading to unlock them.
              </Typography>
            </Box>
          ) : (
            getReadPoems().map((poemId, index) => (
              <React.Fragment key={poemId}>
                {index > 0 && <Divider sx={{ my: 5 }} />}
                <div id={poemId}>
                  <HatchlingPoemDisplay poemId={poemId} />
                </div>
              </React.Fragment>
            ))
          )}
        </div>
      )}

      {/* Toast notification for when a poem is unlocked */}
      {showToast && hatchlingChapterToPoemMapping[chapter.id] && (
        <Toast
          message={`You've unlocked ${
            hatchlingPoems[hatchlingChapterToPoemMapping[chapter.id]]?.title
          }! It's now available in the Poems section.`}
          severity="warning"
          duration={8000}
          onClose={() => setShowToast(false)}
        />
      )}

      <Box
        ref={contentRef}
        sx={{
          fontSize: { xs: "17px", md: "19px" },
          px: { xs: 0, sm: 2, md: 6 },
          "& .MuiTypography-root": {
            mb: 3,
            textIndent: "2em",
            lineHeight: "1.8",
            letterSpacing: "0.01em",
          },
        }}
      >
        {processedContent}
      </Box>
    </Sheet>
  );
};

export default HatchlingReaderContent;
